import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { getCurrentUser, signInWithRedirect } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { ReplaySubject, firstValueFrom } from 'rxjs';
import { clearUserStatusCache } from '../../guards/active-user.guard';
import {
  buildLoginUrl,
  captureReturnUrl,
  clearPendingReturnUrl,
  DEFAULT_POST_LOGIN_URL,
  readPendingReturnUrl,
} from '../../utils/post-login-redirect.util';
import { isDeactivatedUserError } from '../../utils/user-profile-error.util';
import { AuthService } from '../auth/auth.service';
import { UserService } from '../auth/user.service';
import { DateFormatService } from '../date-format/date-format.service';
import { DecimalFormatService } from '../decimal-format/decimal-format.service';
import { IconService } from '../icon/icon.service';
import { ReadOnlyModeService } from '../read-only/read-only.service';
import { WebSocketService } from '../websocket/websocket.service';

@Injectable({
  providedIn: 'root',
})
export class AppInitService {
  errors = new ReplaySubject<Error>(1);

  /**
   * Page the user asked for before being sent to SSO, read while handling the
   * OAuth callback so it can be restored once the tokens are in place.
   */
  private pendingReturnUrl: string | null = null;

  constructor(
    private auth: AuthService,
    private userService: UserService,
    private router: Router,
    private iconService: IconService,
    private dateFormatService: DateFormatService,
    private decimalFormatService: DecimalFormatService,
    private webSocketService: WebSocketService,
    private readOnlyMode: ReadOnlyModeService,
  ) {}

  initializeApp() {
    // Initialize icons first to ensure they're available before components render
    this.iconService.init();

    // Clear user status cache on app initialization to ensure fresh check
    clearUserStatusCache();

    return () =>
      Promise.all([this.handleRefreshSession().then(() => this.initializeAuth(this.auth))])
        .then(async ([isLoggedIn]) => {
          if (isLoggedIn) {
            // Ensure user exists in database and handle first-time user redirect
            // Use a timeout to prevent blocking the app if API is slow
            try {
              const result = await Promise.race([
                firstValueFrom(this.userService.ensureUserExists()),
                // Timeout after 8 seconds - don't block app initialization forever
                new Promise<null>((_, reject) =>
                  setTimeout(() => reject(new Error('ensureUserExists timeout')), 8000),
                ),
              ]);

              if (result && result.user) {
                // Resolve read-only mode from the profile we already have, so
                // action buttons are hidden before the first page renders
                this.readOnlyMode.applyProfile(result.user);

                // Check if user status is inactive and redirect to disable page
                if (result.user.status === 'inactive') {
                  setTimeout(() => {
                    this.router.navigate(['/profile/disable']);
                  }, 100);
                  return; // Exit early to prevent other redirects
                }

                // Initialize date format service with user's preference
                if (result.user.dateFormat) {
                  this.dateFormatService.setFormat(result.user.dateFormat as any);
                }

                // Initialize decimal format service with user's preference
                if (result.user.decimalNotation) {
                  this.decimalFormatService.setFormat(result.user.decimalNotation as any);
                }

                if (result.isFirstTime && !this.pendingReturnUrl) {
                  // First-time user: land on the home page (My Profile), where
                  // they can review the account that was just created. Skipped
                  // when a specific page was requested - that intent wins.
                  setTimeout(() => {
                    this.router.navigate(['/profile']);
                  }, 100);
                }

                // Connect WebSocket for real-time updates
                this.webSocketService.connect();
              }
            } catch (error: any) {
              // Check for deactivated user errors with comprehensive error structure check
              if (isDeactivatedUserError(error)) {
                console.warn('User account is inactive, redirecting to disable page');
                setTimeout(() => {
                  this.router.navigate(['/profile/disable']);
                }, 100);
                return;
              }
              // Log timeout or other errors but don't block app - user can still use the app
              // and user profile will be loaded later by AppComponent
              console.error('Error ensuring user exists:', error);
            }
          }
        })
        .catch((e) => this.errors.next(e));
  }

  /**
   * Handle refresh session flag from Azure logout redirect
   * If the URL contains ?refresh_session=true, force a new login
   */
  private async handleRefreshSession(): Promise<void> {
    const urlParams = new URLSearchParams(window.location.search);
    const isRefreshSession = urlParams.get('refresh_session') === 'true';

    if (isRefreshSession) {
      // Clear the query parameter from URL to prevent infinite loop
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);

      // Force redirect to login with Azure AD
      // signInWithRedirect will redirect the page, but we return void
      // in case it doesn't redirect immediately (e.g., network issues)
      await signInWithRedirect({
        provider: { custom: 'AzureAd' },
        // Come back to the page the user was on, not to the home page
        customState: captureReturnUrl() ?? DEFAULT_POST_LOGIN_URL,
      });
    }
  }

  async initializeAuth(auth: AuthService) {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has('code')) {
      // Standard Amplify OAuth flow
      // The URL contains an OAuth callback code. Amplify automatically exchanges
      // the code for tokens when configured with OAuth (done in AuthService
      // constructor). We must wait for that exchange to finish before proceeding.
      // Read the requested page up front, so that a first-time user is not
      // bounced to /profile below when a specific page was asked for.
      this.pendingReturnUrl = readPendingReturnUrl();

      try {
        await this.waitForOAuthCodeExchange();
        auth.enableOAuth();
        // Clear the code from URL to prevent issues on page refresh, and put the
        // requested page in the address bar while we are at it: this runs before
        // the router performs its initial navigation, so the user goes straight
        // to that page instead of flashing /profile first. When sessionStorage
        // did not survive the SSO round trip (Android shows the IdP in a
        // separate browsing context), the customState Hub listener in
        // AuthService navigates there instead.
        // The customState Hub listener in AuthService re-seeds the stored value
        // while the exchange runs, which is what makes this work when
        // sessionStorage did not survive the round trip.
        this.pendingReturnUrl ??= readPendingReturnUrl();
        window.history.replaceState({}, '', this.pendingReturnUrl ?? window.location.pathname);
        clearPendingReturnUrl();
        return true;
      } catch (error) {
        console.error('OAuth code exchange failed:', error);
        // Clear the code from URL before redirecting to prevent infinite loop
        window.history.replaceState({}, '', window.location.pathname);
        // Keep the requested page across the retry
        window.location.replace(buildLoginUrl(readPendingReturnUrl()));
        // Return resolved promise - the page will redirect anyway
        return false;
      }
    }

    // If already on the login page, skip auth check entirely
    if (window.location.pathname === '/login') {
      return false;
    }

    const isAuthenticated = await auth.isAuthenticated();
    if (!isAuthenticated) {
      // Remember the page that was asked for (handheld/kiosk shortcuts open
      // /operating/loading-verification directly) so the SSO round trip can
      // return to it instead of dropping the user on /profile.
      const returnUrl = captureReturnUrl();
      // Redirect to login page. Use window.location because Angular's router
      // is not fully initialized during APP_INITIALIZER.
      window.location.replace(buildLoginUrl(returnUrl));
      // Return resolved promise - the page will redirect anyway
      // Using never-resolving promise can cause the app to hang if redirect fails
      return false;
    }

    // User is authenticated - enable OAuth for token refresh and signOut
    auth.enableOAuth();
    return true;
  }

  /**
   * Wait for Amplify to complete the OAuth authorization code exchange.
   *
   * When the page loads with ?code= and Amplify is configured with OAuth,
   * the token exchange runs automatically in the background. This method
   * waits for it to finish by:
   *  1. Checking if the user is already authenticated (exchange already done).
   *  2. Listening for Hub 'signInWithRedirect' / 'signInWithRedirect_failure'.
   *  3. Polling getCurrentUser() as a backup if Hub events don't fire.
   */
  private waitForOAuthCodeExchange(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      let pollInterval: ReturnType<typeof setInterval> | null = null;

      const settle = (fn: () => void) => {
        if (!settled) {
          settled = true;
          clearTimeout(timeout);
          if (pollInterval) clearInterval(pollInterval);
          unsubscribe();
          fn();
        }
      };

      // Safety timeout - reduced from 15s to 10s for better UX
      const timeout = setTimeout(() => {
        settle(() => reject(new Error('OAuth code exchange timed out')));
      }, 10_000);

      // Listen for Amplify Hub auth events
      const unsubscribe = Hub.listen('auth', (event) => {
        if (event.payload.event === 'signInWithRedirect') {
          settle(() => resolve());
        } else if (event.payload.event === 'signInWithRedirect_failure') {
          settle(() => reject(new Error('OAuth sign-in redirect failed')));
        }
      });

      // The exchange may have already completed before the listener was
      // attached - check immediately as a fallback.
      getCurrentUser()
        .then(() => settle(() => resolve()))
        .catch(() => {
          // Not yet authenticated; start polling as backup
          // Hub events sometimes don't fire reliably
          pollInterval = setInterval(() => {
            getCurrentUser()
              .then(() => settle(() => resolve()))
              .catch(() => {
                // Still not authenticated, keep polling
              });
          }, 500);
        });
    });
  }
}
